import { useEffect, useState } from 'react';
import { Package, Scan, Check, AlertCircle, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { receivingAPI, storageAPI } from '@/lib/api';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import useAuthStore from '@/store/authStore';

const PendingItemRow = ({ item, onReceive }) => {
  return (
    <TableRow className="hover:bg-slate-50" data-testid={`pending-item-${item.po_line_id}`}>
      <TableCell className="font-mono text-sm">{item.po_number}</TableCell>
      <TableCell>
        <div>
          <p className="font-medium text-sm">{item.part_number}</p>
          <p className="text-xs text-slate-500 truncate max-w-xs">{item.part_description}</p>
        </div>
      </TableCell>
      <TableCell className="text-sm">{item.supplier_name}</TableCell>
      <TableCell className="text-center">
        <span className="font-mono text-sm">{item.quantity_ordered}</span>
      </TableCell>
      <TableCell className="text-center">
        <span className="font-mono text-sm">{item.quantity_received}</span>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant={item.quantity_remaining > 0 ? "secondary" : "outline"}>
          {item.quantity_remaining}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-slate-500">
        {item.expected_date ? formatDate(item.expected_date) : '-'}
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          onClick={() => onReceive(item)}
          disabled={item.quantity_remaining === 0}
          data-testid={`receive-btn-${item.po_line_id}`}
        >
          <Package className="w-4 h-4 mr-1" />
          Receive
        </Button>
      </TableCell>
    </TableRow>
  );
};

const ReceiveDialog = ({ item, open, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [storageLocations, setStorageLocations] = useState([]);
  const [formData, setFormData] = useState({
    quantity_received: item?.quantity_remaining || 1,
    storage_location_id: '',
    condition: 'good',
    notes: '',
  });

  useEffect(() => {
    if (open && user?.branch_id) {
      storageAPI.getAll(user.branch_id).then(res => {
        setStorageLocations(res.data.locations || []);
      });
    }
    if (item) {
      setFormData({
        quantity_received: item.quantity_remaining,
        storage_location_id: '',
        condition: 'good',
        notes: '',
      });
    }
  }, [open, item, user?.branch_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.quantity_received < 1 || formData.quantity_received > item.quantity_remaining) {
      toast.error(`Quantity must be between 1 and ${item.quantity_remaining}`);
      return;
    }

    setLoading(true);
    try {
      await receivingAPI.receive({
        po_line_id: item.po_line_id,
        ...formData,
      });
      toast.success(`Received ${formData.quantity_received} x ${item.part_number}`);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive parts');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Receive Parts</DialogTitle>
          <DialogDescription>
            Record receipt of parts against {item.po_number}
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-slate-50 p-4 rounded-lg mb-4">
          <p className="font-mono text-sm font-medium">{item.part_number}</p>
          <p className="text-sm text-slate-600">{item.part_description}</p>
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span>Ordered: <strong>{item.quantity_ordered}</strong></span>
            <span>Remaining: <strong>{item.quantity_remaining}</strong></span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Quantity to Receive *</Label>
              <Input
                type="number"
                min="1"
                max={item.quantity_remaining}
                value={formData.quantity_received}
                onChange={(e) => setFormData(prev => ({ ...prev, quantity_received: parseInt(e.target.value) || 1 }))}
                data-testid="receive-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Condition</Label>
              <Select value={formData.condition} onValueChange={(v) => setFormData(prev => ({ ...prev, condition: v }))}>
                <SelectTrigger data-testid="receive-condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="partial">Partial/Incomplete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Storage Location</Label>
            <Select value={formData.storage_location_id} onValueChange={(v) => setFormData(prev => ({ ...prev, storage_location_id: v }))}>
              <SelectTrigger data-testid="receive-location">
                <SelectValue placeholder="Select location..." />
              </SelectTrigger>
              <SelectContent>
                {storageLocations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <span className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      {loc.name} {loc.zone && `- ${loc.zone}`}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {storageLocations.length === 0 && (
              <p className="text-xs text-amber-600">No storage locations configured for your branch</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any notes about this receipt..."
              rows={3}
              data-testid="receive-notes"
            />
          </div>

          {formData.condition === 'damaged' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Damaged items will be flagged for review. Please describe the damage in the notes.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="confirm-receive-btn">
              {loading ? 'Receiving...' : 'Confirm Receipt'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function ReceivingPage() {
  const { user } = useAuthStore();
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [receiveDialog, setReceiveDialog] = useState({ open: false, item: null });

  const fetchPending = async () => {
    setLoading(true);
    try {
      const response = await receivingAPI.getPending(user?.branch_id);
      setPendingItems(response.data.pending_items || []);
    } catch (error) {
      console.error('Failed to fetch pending items:', error);
      toast.error('Failed to load pending receipts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const filteredItems = pendingItems.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.po_number.toLowerCase().includes(query) ||
      item.part_number.toLowerCase().includes(query) ||
      item.part_description.toLowerCase().includes(query) ||
      item.supplier_name?.toLowerCase().includes(query)
    );
  });

  const handleReceiveSuccess = () => {
    fetchPending();
  };

  return (
    <div className="space-y-6" data-testid="receiving-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Receiving</h1>
        <p className="text-slate-600">{pendingItems.length} items pending receipt</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Pending Items</p>
            <p className="text-2xl font-bold">{pendingItems.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Unique POs</p>
            <p className="text-2xl font-bold">
              {new Set(pendingItems.map(i => i.po_id)).size}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Units Expected</p>
            <p className="text-2xl font-bold">
              {pendingItems.reduce((sum, i) => sum + i.quantity_remaining, 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by PO, part number, or supplier..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-receiving"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Items Table */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Check className="w-12 h-12 mx-auto text-emerald-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">All caught up!</h3>
            <p className="text-slate-500">
              {searchQuery ? 'No items match your search' : 'No items pending receipt'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider">PO #</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Part</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Supplier</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-center">Ordered</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-center">Received</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-center">Remaining</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Expected</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <PendingItemRow
                      key={item.po_line_id}
                      item={item}
                      onReceive={(item) => setReceiveDialog({ open: true, item })}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receive Dialog */}
      <ReceiveDialog
        item={receiveDialog.item}
        open={receiveDialog.open}
        onClose={() => setReceiveDialog({ open: false, item: null })}
        onSuccess={handleReceiveSuccess}
      />
    </div>
  );
}
